import { useState, useMemo, useCallback, useEffect } from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Smartphone,
  Search,
  Eye,
  Ban,
  ShieldCheck,
  RefreshCcw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Globe,
  Monitor,
  Tablet,
  User,
  Clock,
  CheckCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { useAuth } from '@/components/auth'
import { DeviceService } from '@/domain/device/device.service'
import type { UserDevice } from '@/domain/device/device.types'
import { formatDistanceToNow } from 'date-fns'
import { th } from 'date-fns/locale'

// Page Size Options
const PAGE_SIZES = [10, 20, 50, 100]

// Device Type Icons
const DeviceTypeIcon = ({ type }: { type: string }) => {
  switch (type) {
    case 'mobile':
      return <Smartphone className="h-4 w-4" />
    case 'tablet':
      return <Tablet className="h-4 w-4" />
    default:
      return <Monitor className="h-4 w-4" />
  }
}

export function DevicesPage() {
  // Auth
  const { sessionToken } = useAuth()
  
  // Data State
  const [devices, setDevices] = useState<UserDevice[]>([])
  const [isLoading, setIsLoading] = useState(true)
  
  // Filter State
  const [searchTerm, setSearchTerm] = useState('')
  const [deviceTypeFilter, setDeviceTypeFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [pageSize, setPageSize] = useState(20)
  const [currentPage, setCurrentPage] = useState(1)
  
  // Dialog State
  const [showDetailDialog, setShowDetailDialog] = useState(false)
  const [showBlockDialog, setShowBlockDialog] = useState(false)
  const [selectedDevice, setSelectedDevice] = useState<UserDevice | null>(null)
  const [isActioning, setIsActioning] = useState(false)

  // Load devices
  const loadDevices = useCallback(async () => {
    if (!sessionToken) return

    try {
      setIsLoading(true)
      
      // ✅ Admin: ไม่ส่ง userId → ดึง devices ทั้งหมด
      const allDevices = await DeviceService.getUserDevices(sessionToken, undefined, {
        limit: 1000, // ดึงมากๆ แล้ว filter ฝั่ง frontend
        includeBlocked: true,
      })

      setDevices(allDevices)
    } catch (error: any) {
      console.error('❌ [DevicesPage] Load devices failed:', error)
      toast.error('ไม่สามารถโหลดข้อมูลอุปกรณ์ได้', {
        description: error.message || 'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ',
      })
    } finally {
      setIsLoading(false)
    }
  }, [sessionToken])

  // Load on mount
  useEffect(() => {
    loadDevices()
  }, [loadDevices])

  // Filtered devices
  const filteredDevices = useMemo(() => {
    let filtered = [...devices]

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (device) =>
          device.deviceName?.toLowerCase().includes(term) ||
          device.os?.toLowerCase().includes(term) ||
          device.browser?.toLowerCase().includes(term) ||
          device.ipAddress?.toLowerCase().includes(term) ||
          device.fingerprintId?.toLowerCase().includes(term)
      )
    }

    // Device type filter
    if (deviceTypeFilter !== 'all') {
      filtered = filtered.filter((device) => device.deviceType === deviceTypeFilter)
    }

    // Status filter
    if (statusFilter === 'blocked') {
      filtered = filtered.filter((device) => device.isBlocked)
    } else if (statusFilter === 'trusted') {
      filtered = filtered.filter((device) => device.isTrusted && !device.isBlocked)
    } else if (statusFilter === 'active') {
      filtered = filtered.filter((device) => !device.isBlocked && !device.isTrusted)
    }

    return filtered
  }, [devices, searchTerm, deviceTypeFilter, statusFilter])

  // Pagination
  const totalPages = Math.ceil(filteredDevices.length / pageSize)
  const paginatedDevices = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    const end = start + pageSize
    return filteredDevices.slice(start, end)
  }, [filteredDevices, currentPage, pageSize])

  // Actions
  const handleTrustDevice = async (device: UserDevice) => {
    if (!sessionToken) return

    try {
      setIsActioning(true)
      await DeviceService.trustDevice(sessionToken, device.id)
      toast.success('อนุมัติอุปกรณ์เรียบร้อย')
      await loadDevices()
    } catch (error: any) {
      console.error('❌ [DevicesPage] Trust device failed:', error)
      toast.error('ไม่สามารถอนุมัติอุปกรณ์ได้', {
        description: error.message || 'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ',
      })
    } finally {
      setIsActioning(false)
    }
  }

  const handleBlockDevice = async (device: UserDevice) => {
    if (!sessionToken) return

    try {
      setIsActioning(true)
      await DeviceService.blockDevice(sessionToken, device.id)
      toast.success('บล็อกอุปกรณ์เรียบร้อย')
      setShowBlockDialog(false)
      setSelectedDevice(null)
      await loadDevices()
    } catch (error: any) {
      console.error('❌ [DevicesPage] Block device failed:', error)
      toast.error('ไม่สามารถบล็อกอุปกรณ์ได้', {
        description: error.message || 'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ',
      })
    } finally {
      setIsActioning(false)
    }
  }

  const handleUnblockDevice = async (device: UserDevice) => {
    if (!sessionToken) return

    try {
      setIsActioning(true)
      await DeviceService.unblockDevice(sessionToken, device.id)
      toast.success('ยกเลิกการบล็อกอุปกรณ์เรียบร้อย')
      await loadDevices()
    } catch (error: any) {
      console.error('❌ [DevicesPage] Unblock device failed:', error)
      toast.error('ไม่สามารถยกเลิกการบล็อกอุปกรณ์ได้', {
        description: error.message || 'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ',
      })
    } finally {
      setIsActioning(false)
    }
  }

  // Stats
  const stats = useMemo(() => {
    const total = devices.length
    const blocked = devices.filter((d) => d.isBlocked).length
    const trusted = devices.filter((d) => d.isTrusted && !d.isBlocked).length
    const active = devices.filter((d) => !d.isBlocked && !d.isTrusted).length

    return { total, blocked, trusted, active }
  }, [devices])

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">จัดการอุปกรณ์</h1>
          <p className="text-muted-foreground mt-1">
            ดูและจัดการอุปกรณ์ที่ผู้ใช้ใช้งาน
          </p>
        </div>
        <Button onClick={loadDevices} variant="outline" disabled={isLoading}>
          <RefreshCcw className={cn('h-4 w-4 mr-2', isLoading && 'animate-spin')} />
          รีเฟรช
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-blue-500/10">
                <Smartphone className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">ทั้งหมด</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-green-500/10">
                <ShieldCheck className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">อนุมัติแล้ว</p>
                <p className="text-2xl font-bold text-green-500">{stats.trusted}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-yellow-500/10">
                <Monitor className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">ใช้งานปกติ</p>
                <p className="text-2xl font-bold text-yellow-500">{stats.active}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-red-500/10">
                <Ban className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">ถูกบล็อก</p>
                <p className="text-2xl font-bold text-red-500">{stats.blocked}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table Card */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="ค้นหา ชื่ออุปกรณ์, OS, Browser, IP..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setCurrentPage(1)
                }}
                className="pl-9"
              />
            </div>
            
            {/* Filters */}
            <div className="flex gap-2 flex-wrap">
              <Select value={deviceTypeFilter} onValueChange={setDeviceTypeFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="ประเภท" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">ทั้งหมด</SelectItem>
                  <SelectItem value="desktop">Desktop</SelectItem>
                  <SelectItem value="mobile">Mobile</SelectItem>
                  <SelectItem value="tablet">Tablet</SelectItem>
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="สถานะ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">ทั้งหมด</SelectItem>
                  <SelectItem value="trusted">อนุมัติแล้ว</SelectItem>
                  <SelectItem value="active">ใช้งานปกติ</SelectItem>
                  <SelectItem value="blocked">ถูกบล็อก</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredDevices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Smartphone className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium">ไม่พบข้อมูล</p>
              <p className="text-sm text-muted-foreground mt-1">
                {searchTerm || deviceTypeFilter !== 'all' || statusFilter !== 'all'
                  ? 'ลองเปลี่ยนเงื่อนไขการค้นหา'
                  : 'ยังไม่มีข้อมูลอุปกรณ์'}
              </p>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">ประเภท</TableHead>
                      <TableHead>ชื่ออุปกรณ์</TableHead>
                      <TableHead>ผู้ใช้</TableHead>
                      <TableHead>OS / Browser</TableHead>
                      <TableHead>IP Address</TableHead>
                      <TableHead>Login ครั้ง</TableHead>
                      <TableHead>ครั้งล่าสุด</TableHead>
                      <TableHead>สถานะ</TableHead>
                      <TableHead className="text-right">จัดการ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedDevices.map((device) => (
                      <TableRow key={device.id}>
                        <TableCell>
                          <DeviceTypeIcon type={device.deviceType || 'desktop'} />
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{device.deviceName || 'ไม่ระบุ'}</p>
                            <p className="text-xs text-muted-foreground">
                              {device.screenResolution || 'N/A'}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {device.userUsername ? (
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3 text-muted-foreground" />
                              <span className="text-sm">{device.userUsername}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm">{device.os || 'N/A'}</p>
                            <p className="text-xs text-muted-foreground">
                              {device.browser || 'N/A'}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Globe className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm font-mono">
                              {device.ipAddress || 'N/A'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{device.loginCount || 0}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {(() => {
                              try {
                                if (!device.lastSeenAt) {
                                  return 'N/A';
                                }
                                
                                // Handle different date formats
                                let date: Date;
                                if (typeof device.lastSeenAt === 'string') {
                                  date = new Date(device.lastSeenAt);
                                } else if (device.lastSeenAt instanceof Date) {
                                  date = device.lastSeenAt;
                                } else {
                                  // Try to parse as ISO string
                                  date = new Date(device.lastSeenAt as any);
                                }
                                
                                // Check if date is valid
                                if (!date || isNaN(date.getTime())) {
                                  return 'N/A';
                                }
                                
                                return formatDistanceToNow(date, {
                                  addSuffix: true,
                                  locale: th,
                                });
                              } catch (error) {
                                console.warn('Error formatting lastSeenAt:', device.lastSeenAt, error);
                                return 'N/A';
                              }
                            })()}
                          </div>
                        </TableCell>
                        <TableCell>
                          {device.isBlocked ? (
                            <Badge variant="destructive">ถูกบล็อก</Badge>
                          ) : device.isTrusted ? (
                            <Badge variant="default" className="bg-green-500">
                              <ShieldCheck className="h-3 w-3 mr-1" />
                              อนุมัติแล้ว
                            </Badge>
                          ) : (
                            <Badge variant="outline">ใช้งานปกติ</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setSelectedDevice(device)
                                    setShowDetailDialog(true)
                                  }}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>ดูรายละเอียด</TooltipContent>
                            </Tooltip>

                            {device.isBlocked ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleUnblockDevice(device)}
                                    disabled={isActioning}
                                  >
                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>ยกเลิกการบล็อก</TooltipContent>
                              </Tooltip>
                            ) : (
                              <>
                                {!device.isTrusted && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleTrustDevice(device)}
                                        disabled={isActioning}
                                      >
                                        <ShieldCheck className="h-4 w-4 text-green-500" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>อนุมัติอุปกรณ์</TooltipContent>
                                  </Tooltip>
                                )}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        setSelectedDevice(device)
                                        setShowBlockDialog(true)
                                      }}
                                      disabled={isActioning}
                                    >
                                      <Ban className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>บล็อกอุปกรณ์</TooltipContent>
                                </Tooltip>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <p className="text-sm text-muted-foreground">
                    แสดง {paginatedDevices.length} จาก {filteredDevices.length} รายการ
                  </p>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(value) => {
                      setPageSize(Number(value))
                      setCurrentPage(1)
                    }}
                  >
                    <SelectTrigger className="w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZES.map((size) => (
                        <SelectItem key={size} value={size.toString()}>
                          {size} / หน้า
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <p className="text-sm">
                    หน้า {currentPage} / {totalPages}
                  </p>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>รายละเอียดอุปกรณ์</DialogTitle>
            <DialogDescription>
              ข้อมูลอุปกรณ์และประวัติการใช้งาน
            </DialogDescription>
          </DialogHeader>
          {selectedDevice && (
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="basic">ข้อมูลพื้นฐาน</TabsTrigger>
                <TabsTrigger value="fingerprint">Fingerprint</TabsTrigger>
                <TabsTrigger value="raw">Raw Data</TabsTrigger>
              </TabsList>
              
              <TabsContent value="basic" className="space-y-4">
                <ScrollArea className="h-[500px] pr-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">ชื่ออุปกรณ์</p>
                      <p className="text-base">{selectedDevice.deviceName || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">ประเภท</p>
                      <div className="flex items-center gap-2">
                        <DeviceTypeIcon type={selectedDevice.deviceType || 'desktop'} />
                        <p className="text-base capitalize">
                          {selectedDevice.deviceType || 'desktop'}
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">OS</p>
                      <p className="text-base">{selectedDevice.os || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Browser</p>
                      <p className="text-base">{selectedDevice.browser || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Screen Resolution</p>
                      <p className="text-base">{selectedDevice.screenResolution || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">IP Address</p>
                      <p className="text-base font-mono">{selectedDevice.ipAddress || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Timezone</p>
                      <p className="text-base">{selectedDevice.timezone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Language</p>
                      <p className="text-base">{selectedDevice.language || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Color Depth</p>
                      <p className="text-base">{selectedDevice.colorDepth ? `${selectedDevice.colorDepth} bit` : 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Pixel Depth</p>
                      <p className="text-base">{selectedDevice.pixelDepth ? `${selectedDevice.pixelDepth} bit` : 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Device Memory</p>
                      <p className="text-base">{selectedDevice.deviceMemory ? `${selectedDevice.deviceMemory} GB` : 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Hardware Concurrency</p>
                      <p className="text-base">{selectedDevice.hardwareConcurrency ? `${selectedDevice.hardwareConcurrency} cores` : 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Max Touch Points</p>
                      <p className="text-base">{selectedDevice.maxTouchPoints !== undefined ? selectedDevice.maxTouchPoints : 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Fingerprint ID</p>
                      <p className="text-base font-mono text-xs break-all">
                        {selectedDevice.fingerprintId || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Login ครั้ง</p>
                      <p className="text-base">{selectedDevice.loginCount || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">ครั้งแรกที่เห็น</p>
                      <p className="text-base">
                        {selectedDevice.firstSeenAt
                          ? (() => {
                              try {
                                const date = new Date(selectedDevice.firstSeenAt);
                                if (isNaN(date.getTime())) {
                                  return 'N/A';
                                }
                                return date.toLocaleString('th-TH');
                              } catch (error) {
                                return 'N/A';
                              }
                            })()
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">ครั้งล่าสุด</p>
                      <p className="text-base">
                        {selectedDevice.lastSeenAt
                          ? (() => {
                              try {
                                const date = new Date(selectedDevice.lastSeenAt);
                                if (isNaN(date.getTime())) {
                                  return 'N/A';
                                }
                                return date.toLocaleString('th-TH');
                              } catch (error) {
                                return 'N/A';
                              }
                            })()
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">สถานะ</p>
                      <div className="mt-1">
                        {selectedDevice.isBlocked ? (
                          <Badge variant="destructive">ถูกบล็อก</Badge>
                        ) : selectedDevice.isTrusted ? (
                          <Badge variant="default" className="bg-green-500">
                            <ShieldCheck className="h-3 w-3 mr-1" />
                            อนุมัติแล้ว
                          </Badge>
                        ) : (
                          <Badge variant="outline">ใช้งานปกติ</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="fingerprint" className="space-y-4">
                <ScrollArea className="h-[500px] pr-4">
                  {selectedDevice.rawFingerprint ? (
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-2">Visitor ID</p>
                        <p className="text-sm font-mono break-all bg-muted p-2 rounded">
                          {selectedDevice.rawFingerprint.visitorId || selectedDevice.fingerprintId || 'N/A'}
                        </p>
                      </div>
                      
                      <Separator />
                      
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-2">Components</p>
                        <div className="space-y-2">
                          {selectedDevice.rawFingerprint.components && Object.entries(selectedDevice.rawFingerprint.components).map(([key, component]: [string, any]) => (
                            <div key={key} className="bg-muted p-3 rounded">
                              <p className="text-xs font-medium text-muted-foreground mb-1">{key}</p>
                              <p className="text-sm">
                                {typeof component === 'object' && component !== null
                                  ? component.value !== undefined
                                    ? String(component.value)
                                    : JSON.stringify(component)
                                  : String(component)}
                              </p>
                              {typeof component === 'object' && component !== null && component.error && (
                                <p className="text-xs text-red-500 mt-1">Error: {component.error}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {selectedDevice.rawFingerprint.meta && (
                        <>
                          <Separator />
                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-2">Meta Information</p>
                            <div className="space-y-2">
                              {Object.entries(selectedDevice.rawFingerprint.meta).map(([key, value]: [string, any]) => (
                                <div key={key} className="flex justify-between items-center py-1">
                                  <p className="text-xs font-medium text-muted-foreground">{key}</p>
                                  <p className="text-sm">{String(value)}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <p className="text-muted-foreground">ไม่มีข้อมูล Fingerprint</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        อุปกรณ์นี้อาจถูกสร้างก่อนการอัปเดตระบบ
                      </p>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="raw" className="space-y-4">
                <ScrollArea className="h-[500px] pr-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Raw JSON Data</p>
                    <pre className="text-xs bg-muted p-4 rounded overflow-auto">
                      {JSON.stringify(selectedDevice, null, 2)}
                    </pre>
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Block Confirmation Dialog */}
      <AlertDialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันการบล็อกอุปกรณ์</AlertDialogTitle>
            <AlertDialogDescription>
              คุณแน่ใจหรือไม่ว่าต้องการบล็อกอุปกรณ์นี้? 
              ผู้ใช้จะไม่สามารถใช้งานระบบจากอุปกรณ์นี้ได้อีก
              {selectedDevice && (
                <div className="mt-2 p-2 bg-muted rounded">
                  <p className="text-sm font-medium">{selectedDevice.deviceName}</p>
                  <p className="text-xs text-muted-foreground">
                    {selectedDevice.os} / {selectedDevice.browser}
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isActioning}>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedDevice && handleBlockDevice(selectedDevice)}
              disabled={isActioning}
              className="bg-red-500 hover:bg-red-600"
            >
              {isActioning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  กำลังบล็อก...
                </>
              ) : (
                'บล็อกอุปกรณ์'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

